import React, { useState, useEffect, useRef } from 'react';
import InputPanel from './InputPanel';
import HeroGauge from './HeroGauge';
import ScenarioTable from './ScenarioTable';
import RevenueWaterfall from './RevenueWaterfall';
import AECapacityTable from './AECapacityTable';
import WaterfallReport from './WaterfallReport';
import ProjectionOverview from './ProjectionOverview';
import { calculateWaterfall, generateScenarios, waterfallToForecast } from '../utils/waterfallCalculator';
import { FY26_WATERFALL_DATA, FY26_FORECAST_DATA } from '../utils/FY26StaticData';
import { useAuth } from '../context/AuthContext';

function Dashboard({ onAdminClick }) {
  const { logout, userEmail } = useAuth();
  const [forecastData, setForecastData] = useState(null);
  const [waterfallData, setWaterfallData] = useState(null);
  const [showInputPanel, setShowInputPanel] = useState(false);
  const [metrics, setMetrics] = useState({
    startingArr: 10522000,       // $10.522M starting ARR (Dec 2025)
    arpa: 194000,                // $194K ARPA
    sqlToSqoRate: 0.434,         // 43.4% SQL → SQO
    sqoToCwRate: 0.288,          // 28.8% SQO → CW
    pipelineCoverageRatio: 3.5,  // 3.5x pipeline coverage
    startingSqls: 55,            // Starting SQLs (Jan 2026 - adjusted for Q1 $4.2M target)
    monthlyGrowthRate: 0.10,     // ~10% monthly growth
    decemberSurge: 25,           // December year-end surge
    spreadMultiplier: 1.0        // Scenario spread
  });
  const [spreadMultiplier, setSpreadMultiplier] = useState(1.0);
  const debounceTimerRef = useRef(null);

  const handleDataLoad = (parsedData, customMetrics = null) => {
    // Generate scenarios from parsed CSV data
    console.log('Dashboard received parsed data:', parsedData);
    try {
      if (!parsedData || parsedData.length === 0) {
        console.error('No data provided');
        return;
      }

      // Create scenarios: base (as-is), worst (75%), best (125%)
      const baseScenario = parsedData.map(row => ({
        month: row.month,
        sqls: row.sqls || row.SQL || 0,
        sqos: row.sqos || row.SQO || 0,
        closedWon: row.closedWon || row['Closed Won'] || 0,
        revenue: row.revenue || 0,
        cumulativeRevenue: row.cumulativeRevenue || 0,
        pipeline: row.pipeline || 0,
        isHistorical: false
      }));

      const worstScenario = parsedData.map(row => ({
        month: row.month,
        sqls: Math.round((row.sqls || row.SQL || 0) * 0.75),
        sqos: Math.round((row.sqos || row.SQO || 0) * 0.75),
        closedWon: Math.round((row.closedWon || row['Closed Won'] || 0) * 0.75),
        revenue: Math.round((row.revenue || 0) * 0.75),
        cumulativeRevenue: 0,
        pipeline: (row.pipeline || 0) * 0.75,
        isHistorical: false
      }));

      const bestScenario = parsedData.map(row => ({
        month: row.month,
        sqls: Math.round((row.sqls || row.SQL || 0) * 1.25),
        sqos: Math.round((row.sqos || row.SQO || 0) * 1.25),
        closedWon: Math.round((row.closedWon || row['Closed Won'] || 0) * 1.25),
        revenue: Math.round((row.revenue || 0) * 1.25),
        cumulativeRevenue: 0,
        pipeline: (row.pipeline || 0) * 1.25,
        isHistorical: false
      }));

      // Calculate cumulative revenues
      let cumulative = 0;
      worstScenario.forEach(row => {
        cumulative += row.revenue;
        row.cumulativeRevenue = cumulative;
      });

      cumulative = 0;
      bestScenario.forEach(row => {
        cumulative += row.revenue;
        row.cumulativeRevenue = cumulative;
      });

      const forecasts = {
        base: baseScenario,
        best: bestScenario,
        worst: worstScenario,
        currentRevenue: 7400000,
        targets: [
          { name: '$10M by EOY 2025', value: 10000000, date: '2025-12', baseline: 7400000 },
          { name: '$20M by H1 2026', value: 20000000, date: '2026-06', baseline: 10000000 },
          { name: '$20M additional by H2 2026', value: 40000000, date: '2026-12', baseline: 20000000 }
        ]
      };

      console.log('Generated scenarios:', forecasts);
      setForecastData(forecasts);
      setShowInputPanel(false); // Hide input panel when data is loaded
      console.log('Forecast data set successfully!');
    } catch (err) {
      console.error('Error generating forecasts:', err);
      throw err;
    }
  };

  const handleMetricsChange = (newMetrics) => {
    console.log('Metrics changed:', newMetrics);
    setMetrics(newMetrics);
    // Regenerate forecast with new metrics based on default data
    try {
      generateForecastsFromMetrics(newMetrics);
    } catch (err) {
      console.error('Error regenerating scenarios:', err);
    }
  };

  const handleSpreadMultiplierChange = (newSpread) => {
    setSpreadMultiplier(newSpread);
    const updatedMetrics = { ...metrics, spreadMultiplier: newSpread };
    setMetrics(updatedMetrics);
    try {
      generateForecastsFromMetrics(updatedMetrics);
    } catch (err) {
      console.error('Error regenerating scenarios:', err);
    }
  };

  const handleResetData = () => {
    console.log('Resetting to FY26 static data');
    // Reset to static FY26 forecast data
    setWaterfallData(FY26_WATERFALL_DATA);
    setForecastData(FY26_FORECAST_DATA);
    // Reset metrics to FY26 defaults (adjusted for Q1 $4.2M, Q2 $5.8M targets)
    const fy26Metrics = {
      startingArr: 10522000,
      arpa: 194000,
      sqlToSqoRate: 0.434,
      sqoToCwRate: 0.288,
      pipelineCoverageRatio: 3.5,
      startingSqls: 55,
      monthlyGrowthRate: 0.10,
      decemberSurge: 25,
      spreadMultiplier: 1.0
    };
    setMetrics(fy26Metrics);
    setSpreadMultiplier(1.0);
  };

  const generateForecastsFromMetrics = (currentMetrics) => {
    console.log('Generating forecasts with metrics:', currentMetrics);

    // Calculate waterfall using the new calculator
    const waterfallResult = calculateWaterfall(currentMetrics);
    setWaterfallData(waterfallResult);

    // Generate scenario variations (worst, base, best)
    const scenarios = generateScenarios(currentMetrics, currentMetrics.spreadMultiplier);

    // Convert scenarios to forecast format
    const baseScenario = waterfallToForecast(scenarios.base);
    const worstScenario = waterfallToForecast(scenarios.worst);
    const bestScenario = waterfallToForecast(scenarios.best);

    const forecasts = {
      base: baseScenario,
      best: bestScenario,
      worst: worstScenario,
      currentRevenue: currentMetrics.startingArr,
      targets: [
        { name: '$10M by EOY 2025', value: 10000000, date: '2025-12', baseline: currentMetrics.startingArr },
        { name: '$20M by H1 2026', value: 20000000, date: '2026-06', baseline: 10000000 },
        { name: '$40M by EOY 2026', value: 40000000, date: '2026-12', baseline: 20000000 }
      ]
    };

    setForecastData(forecasts);
    console.log('Generated forecasts:', forecasts);
    console.log('Generated waterfall:', waterfallResult);
  };

  // Initialize with static FY26 forecast data
  useEffect(() => {
    try {
      // Set the static waterfall and forecast data
      setWaterfallData(FY26_WATERFALL_DATA);
      setForecastData(FY26_FORECAST_DATA);
      console.log('Loaded static FY26 forecast data');
    } catch (err) {
      console.error('Error loading forecast data:', err);
    }
  }, []);

  // No longer used - waterfall updates via metrics changes
  // eslint-disable-next-line no-unused-vars
  const handleWaterfallDataChange = (waterfallData) => {
    // Debounce the update to avoid excessive recalculations
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      try {
        // Create forecast scenarios directly from waterfall data
        const detailRows = waterfallData.filter(row => !row.isTotal);

        const monthNameToNum = {
          'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04', 'May': '05', 'Jun': '06',
          'Jul': '07', 'Aug': '08', 'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
        };

        const convertMonthCode = (monthStr) => {
          const [name, year] = monthStr.split(' ');
          return `${year}-${monthNameToNum[name]}`;
        };

        // Base scenario: use waterfall data as-is
        const baseScenario = detailRows.map(row => ({
          month: convertMonthCode(row.month),
          sqls: row.sqls,
          sqos: row.sqos,
          closedWon: row.closedWon,
          revenue: row.monthlyArr,
          cumulativeRevenue: row.cumulativeArr,
          pipeline: row.pipeline,
          isHistorical: false
        }));

        // Worst scenario: reduce by 25%
        const worstScenario = detailRows.map(row => ({
          month: convertMonthCode(row.month),
          sqls: Math.round(row.sqls * 0.75),
          sqos: Math.round(row.sqos * 0.75),
          closedWon: Math.round(row.closedWon * 0.75),
          revenue: Math.round(row.monthlyArr * 0.75),
          cumulativeRevenue: 0,
          pipeline: row.pipeline * 0.75,
          isHistorical: false
        }));

        // Best scenario: increase by 25%
        const bestScenario = detailRows.map(row => ({
          month: convertMonthCode(row.month),
          sqls: Math.round(row.sqls * 1.25),
          sqos: Math.round(row.sqos * 1.25),
          closedWon: Math.round(row.closedWon * 1.25),
          revenue: Math.round(row.monthlyArr * 1.25),
          cumulativeRevenue: 0,
          pipeline: row.pipeline * 1.25,
          isHistorical: false
        }));

        // Calculate cumulative revenues
        let cumulative = 0;
        worstScenario.forEach(row => {
          cumulative += row.revenue;
          row.cumulativeRevenue = cumulative;
        });

        cumulative = 0;
        bestScenario.forEach(row => {
          cumulative += row.revenue;
          row.cumulativeRevenue = cumulative;
        });

        const forecasts = {
          base: baseScenario,
          best: bestScenario,
          worst: worstScenario,
          currentRevenue: 7400000,
          targets: [
            { name: '$10M by EOY 2025', value: 10000000, date: '2025-12', baseline: 7400000 },
            { name: '$20M by H1 2026', value: 20000000, date: '2026-06', baseline: 10000000 },
            { name: '$20M additional by H2 2026', value: 40000000, date: '2026-12', baseline: 20000000 }
          ]
        };

        setForecastData(forecasts);
      } catch (err) {
        console.error('Error updating forecast from waterfall:', err);
      }
    }, 500); // 500ms debounce
  };


  return (
    <div className="dashboard flex flex-col gap-6 p-6 bg-gray-100 min-h-screen">
      {/* Header Section */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-4">Revenue Projection Dashboard</h1>
        </div>
        <button
          onClick={logout}
          className="bg-gray-400 text-white px-3 py-1 rounded text-xs font-medium hover:bg-gray-500 transition-colors"
        >
          Logout
        </button>
      </div>

      {/* Revenue Waterfall + Data Input Row */}
      <div className="flex gap-6">
        {/* Left: Revenue Forecast Waterfall */}
        <div className="flex-1">
          <RevenueWaterfall waterfallData={waterfallData} />
        </div>

        {/* Right: Data Input Panel */}
        {forecastData && (
          <div className="w-80 h-fit sticky top-6">
            <InputPanel
              onDataLoad={handleDataLoad}
              metrics={metrics}
              onMetricsChange={handleMetricsChange}
              onReset={handleResetData}
            />
          </div>
        )}
      </div>

      {/* Hero Gauge - Only show if forecast data loaded */}
      {forecastData && (
        <div>
          <HeroGauge forecastData={forecastData} />
        </div>
      )}

      {/* Projection Overview - FY26 Strategy & Assumptions */}
      <div>
        <ProjectionOverview />
      </div>

      {/* Scenario Table - Only show if forecast data loaded */}
      {forecastData && (
        <div>
          <ScenarioTable forecastData={forecastData} />
        </div>
      )}

      {/* Waterfall Report - Full Width Report with Visualization and Metrics */}
      {forecastData && (
        <div>
          <WaterfallReport forecastData={forecastData} />
        </div>
      )}

      {/* AE Capacity Table - Full Width Below */}
      {forecastData && (
        <div>
          <AECapacityTable forecastData={forecastData} />
        </div>
      )}

      {/* Show toggle button if input panel is hidden */}
      {!showInputPanel && !forecastData && (
        <div className="mb-6">
          <button
            onClick={() => setShowInputPanel(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 transition-colors"
          >
            <span>📁</span>
            Load New Data
          </button>
        </div>
      )}

      {/* Scenario Spread - End of Page */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="max-w-2xl">
          <label className="text-sm font-medium text-gray-700 block mb-2">
            Scenario Spread: <span className="text-green-700 font-semibold">{spreadMultiplier.toFixed(2)}x</span> <span className="text-gray-500">(Default: 1.0x)</span>
          </label>
          <input
            type="range"
            min="0.25"
            max="2"
            step="0.25"
            value={spreadMultiplier}
            onChange={(e) => handleSpreadMultiplierChange(parseFloat(e.target.value))}
            className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-xs text-gray-500 mt-2">Controls differentiation between worst/best cases</div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
